import { scaleLinear } from '@visx/scale';
import { LinePath } from '@visx/shape';
import React, { useMemo } from 'react';

/** Why a trend cell is empty. Said in the reader's terms, not the pipeline's. */
export const SPARKLINE_UNMEASURED_HINT = 'Not enough history yet to show a trend.';

/**
 * A one-cell trend line.
 *
 * Composed from the same visx primitives every other chart here uses rather than a new dependency
 * or hand-rolled path arithmetic, so the scale conversion stays in the library.
 *
 * A missing bucket is a GAP, never a point at zero: "not cited in this window" and "no measurement
 * for this window" are different findings, and a line dropped to the axis asserts the first. With
 * fewer than two measured points there is no trend at all and the cell says so instead of drawing
 * a flat line that would read as "steady".
 */
export function GovernanceSparkline({
  values,
  color,
  width = 72,
  height = 20,
  label,
}: {
  /** One value per bucket, oldest first. `undefined` marks a bucket with no measurement. */
  values: readonly (number | undefined)[];
  color: string;
  width?: number;
  height?: number;
  /** Read out to assistive technology, and shown on hover. */
  label?: string;
}) {
  const points = useMemo(() => values.map((value, index) => ({ index, value })), [values]);
  const measured = useMemo(() => points.filter((point) => point.value !== undefined), [points]);

  const scales = useMemo(() => {
    if (measured.length < 2) {
      return undefined;
    }

    const numbers = measured.map((point) => point.value as number);
    const highest = Math.max(...numbers);
    const lowest = Math.min(...numbers);
    // A flat series still deserves a line through the middle rather than one pinned to an edge.
    const padding = highest === lowest ? Math.max(1, highest * 0.1) : (highest - lowest) * 0.15;

    return {
      x: scaleLinear<number>({ domain: [0, Math.max(1, points.length - 1)], range: [1, width - 1] }),
      y: scaleLinear<number>({ domain: [lowest - padding, highest + padding], range: [height - 2, 2] }),
    };
  }, [measured, points.length, width, height]);

  if (!scales) {
    return (
      <span className="text-slate-300" title={SPARKLINE_UNMEASURED_HINT} aria-label={SPARKLINE_UNMEASURED_HINT}>
        —
      </span>
    );
  }

  return (
    <svg width={width} height={height} role="img" aria-label={label ?? 'Trend'} focusable="false">
      <title>{label ?? 'Trend'}</title>
      <LinePath
        data={points}
        // Gaps rather than a drop to the axis wherever a bucket has no measurement.
        defined={(point) => point.value !== undefined}
        x={(point) => scales.x(point.index)}
        y={(point) => scales.y(point.value as number)}
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}
